import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { distinctUntilChanged, fromEvent, map, startWith } from 'rxjs';

import { CustomPropertyKeys } from '@reach/interfaces/custom-property/custom-property-id-keys';
import { AnyCustomPropertyValue } from '@reach/interfaces/custom-property/custom-property-value.interface';
import { NodeType } from '@reach/interfaces/nodes/node-type.enum';
import { ensureAssert } from '@reach/utils/assertions/ensure-assert';

import { CommonButtonsModule } from '~ng-common-lib/buttons';
import { injectSrcConverter } from '~ng-common-lib/cdk/img';
import { ScreenService, ScreenSize } from '~ng-common-lib/cdk/screen.service';
import { MainClientUrlPipe } from '~ng-common-lib/core/pipes/main-client-url.pipe';
import { COMMON_ENVIRONMENT } from '~ng-common-lib/core/tokens/environment';
import { CommonNodeThumbnailComponent } from '~ng-common-lib/nodes/components/node-thumbnail/node-thumbnail.component';
import { NodeAsTypeModule } from '~ng-common-lib/nodes/pipes/node-as-type.pipe';
import { ClientNode } from '~ng-common-lib/nodes/types/node';
import { AudioPreviewComponent } from '~ng-common-lib/previews/components/audio/audio-preview.component';
import { ImagePreviewComponent } from '~ng-common-lib/previews/components/image/image.component';
import { PdfPreviewComponent } from '~ng-common-lib/previews/components/pdf/pdf-preview.component';
import { VideoPreviewComponent } from '~ng-common-lib/previews/components/video/video-preview.component';
import { injectConfiguration } from '~ng-common-lib/source-preview/tokens/configuration';
import { NodeTypeIconDirective } from '~ng-common-lib/ui/icons/node-type-icon.directive';
import { MenusModule } from '~ng-common-lib/ui/menus';
import { TooltipsModule } from '~ng-common-lib/ui/tooltips';

import { SourcePreviewService } from '../../services/source-preview.service';
import {
	CREATION_DATE_KEYS,
	SourcePreviewByNode,
	SourcePreviewByNodeSource,
	SourcePreviewType,
} from '../../types/source-preview-input';
import { SourcePreviewWithSourcesComponent } from '../preview-with-sources/preview-with-sources.component';

function getOldestCreationDateMs(customProperties: AnyCustomPropertyValue[]): number | undefined {
	const datesMs = CREATION_DATE_KEYS.map(
		(key) => customProperties.find((p) => p.key === key)?.value
	).filter((value): value is number => typeof value === 'number' && Number.isFinite(value));

	return datesMs.length > 0 ? Math.min(...datesMs) : undefined;
}

export enum ClientScreen {
	MOBILE = 'MOBILE',
	DESKTOP = 'DESKTOP',
}

@Component({
	selector: 'source-preview-modal',
	templateUrl: './source-preview-modal.component.html',
	styleUrls: ['./source-preview-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		RouterModule,
		CommonButtonsModule,
		CommonNodeThumbnailComponent,
		PdfPreviewComponent,
		AudioPreviewComponent,
		VideoPreviewComponent,
		ImagePreviewComponent,
		NodeAsTypeModule,
		SourcePreviewWithSourcesComponent,
		MainClientUrlPipe,
		NodeTypeIconDirective,
		MenusModule,
		TooltipsModule,
	],
	host: {
		class: 'top-0 left-0 w-full h-full block absolute     ',
		style: 'z-index: 10;',
	},
})
export class SourcePreviewModalComponent {
	public get screen(): ClientScreen {
		return window.visualViewport?.width !== undefined && window.visualViewport.width >= 720
			? ClientScreen.DESKTOP
			: ClientScreen.MOBILE;
	}

	protected readonly NodeType = NodeType;
	protected readonly SourcePreviewType = SourcePreviewType;
	protected readonly ScreenSize = ScreenSize;

	public readonly configuration = injectConfiguration();
	private readonly sourcePreviewService = inject(SourcePreviewService);
	private readonly screenService = inject(ScreenService);
	private readonly environment = inject(COMMON_ENVIRONMENT);
	private readonly srcConverter = injectSrcConverter();

	public readonly showDownloadButton = input(true);

	protected readonly $smallCardExpanded = signal(false);
	protected readonly $showingAllSourcesMenu = signal(false);
	protected readonly $showNameTooltip = signal(false);

	private readonly $sourcesByNode = this.sourcePreviewService.$sources;
	protected readonly $selected = this.sourcePreviewService.$selected;
	protected readonly $screen = this.screenService.$screen;

	public readonly size$ = window.visualViewport
		? fromEvent(window.visualViewport, 'resize').pipe(
				startWith(''),
				map(() => {
					return window.visualViewport!.width;
				})
			)
		: fromEvent(window, 'resize').pipe(
				startWith(''),
				map(() => {
					return window.innerWidth;
				})
			);

	public readonly screen$ = this.size$.pipe(
		map(() => {
			return this.screen;
		}),
		distinctUntilChanged()
	);

	public readonly $isDesktop = toSignal(
		this.screen$.pipe(
			map((screen) => {
				return screen === ClientScreen.DESKTOP;
			})
		)
	);
	public readonly customDownloadLogic = !!this.configuration.downloadLogic;

	protected readonly $smallScreen = computed(() => {
		const screen = this.$screen();
		return screen === ScreenSize.SMALL;
	});

	protected readonly $modalBoxClass = computed(() => {
		if (this.configuration.modalFullscreen) {
			return 'w-full h-full max-w-full max-h-full';
		}
		if (this.$smallScreen()) {
			return 'w-full h-[calc(100%-32px)] max-w-full max-h-[calc(100%-48px)] rounded-t-3';
		}
		return 'w-135 h-90 max-w-[calc(100%-128px)] max-h-[calc(100%-64px)] border border-line-250 rounded-3';
	});

	public readonly $selectedItem = computed(() => {
		const nodes = this.$sourcesByNode();
		const rawSelected = this.$selected();

		const selectedNodeId = rawSelected?.nodeId ?? nodes[0]?.node.id ?? null;
		const node = nodes.find((item) => item.node.id === selectedNodeId) ?? null;

		if (!ensureAssert(node)) return null;

		const verifiedProperty = node.node.customProperties.find(
			(property) => property.key === CustomPropertyKeys.CERTIFIED
		);

		return {
			...node,
			certified: verifiedProperty ?? false,
			creationFileDate: this.formatOldestCreationDate(node.node.customProperties ?? []),
		};
	});

	public readonly $selectedSourceId = computed(
		() => {
			const selected = this.$selected();
			const item = this.$selectedItem();

			const source = item?.sources.find((source) => source.id === selected?.sourceId);

			if (source) {
				return source.id;
			}

			return item?.sources[0]?.id ?? null;
		},
		{
			equal: () => {
				// This is a hack so we can select the same id multiple times to focus on it.
				return false;
			},
		}
	);

	// This is a hack so we can select the same id multiple times to focus on it.
	public readonly $selectedSourceIdAsObj = computed(() => {
		const id = this.$selectedSourceId();
		return { id };
	});

	public readonly $rightItems = computed(() => {
		const nodes = this.$sourcesByNode();
		const selectedItem = this.$selectedItem();

		return nodes.map((item) => {
			const verifiedProperty = item.node.customProperties.find(
				(property) => property.key === CustomPropertyKeys.CERTIFIED
			);
			return {
				...item,
				selected: item.node.id === selectedItem?.node.id,
				certified: verifiedProperty ?? false,
				creationFileDate: this.formatOldestCreationDate(item.node.customProperties ?? []),
			};
		});
	});

	public readonly $selectedFile = computed(() => {
		const selected = this.$selectedItem();

		if (selected?.node.nodeType === NodeType.FILE) {
			return selected as SourcePreviewByNode<NodeType.FILE>;
		}

		return null;
	});

	public readonly canOpenNodeCurrentClient = (() => {
		try {
			const clientUrl = new URL(this.environment.mainClient.url);
			const currentUrl = new URL(window.location.href);

			return clientUrl.host === currentUrl.host;
		} catch (error) {
			return false;
		}
	})();

	public openNode(nodeId: string): void {
		this.sourcePreviewService.$selected.set({
			nodeId,
			sourceId: null,
		});
	}

	public selectSource(sourceToSelect: SourcePreviewByNodeSource): void {
		const prevSelected = this.$selectedItem();

		if (!prevSelected) {
			return;
		}

		this.sourcePreviewService.$selected.update((prev) => {
			const existing = prevSelected?.sources.find(({ id }) => id === sourceToSelect.id);

			if (!existing) {
				return prev;
			}

			return {
				nodeId: prevSelected.node.id,
				sourceId: sourceToSelect.id,
			};
		});
	}

	public closeModal(): void {
		this.sourcePreviewService.close();
	}

	public customDownload(file: ClientNode<NodeType.FILE>): void {
		this.configuration.downloadLogic?.(this.srcConverter(file.data.fileUrl), file.name);
	}

	protected fileUrl(file: ClientNode<NodeType.FILE>): string {
		return this.srcConverter(file.data.fileUrl);
	}

	protected toggleSmallScreen(): void {
		this.$smallCardExpanded.update((prev) => !prev);
	}

	protected toggleShowingAllSourcesMenu(): void {
		this.$showingAllSourcesMenu.update((prev) => !prev);
	}

	protected updateNameTooltip(element: HTMLElement): void {
		this.$showNameTooltip.set(element.scrollWidth > element.clientWidth);
	}

	private formatOldestCreationDate(
		customProperties: AnyCustomPropertyValue[]
	): string | undefined {
		const oldestMs = getOldestCreationDateMs(customProperties);

		return oldestMs !== undefined ? this.dateTransform(oldestMs) : undefined;
	}

	private dateTransform(timeInMiliseconds: number): string {
		try {
			const date = new Date(timeInMiliseconds);
			const formattedDate = Intl.DateTimeFormat('default').format(date);

			return formattedDate;
		} catch (e) {
			console.error(timeInMiliseconds, e);
			return 'Invalid Date';
		}
	}
}
